import { useEffect, useMemo } from "react";
import { useForm, useFormState } from "react-hook-form";

import {
  FormInput,
  InputDate,
  SelectForm,
  SelectOptionTypes,
} from "../../../../components/Input";
import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  FormPanel,
  LabelRow,
  discardCopyOf,
  formPanelStatusOf,
} from "../../../../components/FormPanel";
import {
  buildDraftDetails,
  ConfirmDetail,
  joinDetailParts,
} from "../../../../components/PopUp/confirmShared";
import {
  useGetAccountsQuery,
  useGetInvestmentTypesQuery,
} from "../../../../services";
import { SETTLEMENT_ACCOUNT_TYPES } from "../../../Accounts/types";
import { CashSettlement, InvestmentsTypeOptions } from "../../types";
import {
  TRACKING_MODE_DESCRIPTIONS,
  TRACKING_MODE_IMMUTABLE_NOTE,
  TRACKING_MODE_LABELS,
} from "../../constants";
import { DateFormat, formatDate } from "../../../../utils";
import { MobileInvestmentForm } from "./MobileInvestmentForm";
import {
  DEFAULT_INVESTMENT,
  FORM_ID,
  TRACKING_MODE_OPTIONS,
} from "./constants";
import { CashSettlementField } from "./CashSettlementField";
import {
  InvestmentFormValues,
  InvestmentPanelProps,
  SettlementFieldState,
} from "./types";
import { ReadOnlyValue } from "./styles";

const TYPES_ERROR_MESSAGE =
  "Failed to load investment types. Please try again.";

export const InvestmentPanel = ({
  investment,
  isOpen,
  onClose,
  handleOnSubmit,
  trackingMode,
  hasLedgerCommands = false,
}: InvestmentPanelProps) => {
  const {
    data: investmentTypesData,
    isFetching,
    isError,
    refetch,
  } = useGetInvestmentTypesQuery();
  const investmentTypes: InvestmentsTypeOptions[] = useMemo(
    () =>
      (investmentTypesData ?? []).map((type) => ({
        value: String(type.id),
        label: type.name,
      })),
    [investmentTypesData],
  );
  const hasNoTypes = investmentTypesData === undefined;
  const isLoadingTypes = isFetching && hasNoTypes;
  const isTypesError = isError && hasNoTypes;

  const resetValues = investment ?? DEFAULT_INVESTMENT;

  const form = useForm<InvestmentFormValues>({
    defaultValues: resetValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, getValues, setValue, register, clearErrors, watch } = form;

  const { isSubmitting } = useFormState({ control });

  const watchedName = watch("name");
  const watchedTrackingMode = watch("trackingMode");
  const watchedSettlement = watch("cashSettlement");
  const watchedSettlementAccount = watch("settlementAccount");
  const isEdit = Boolean(investment);
  const isSettlementShown =
    (isEdit ? trackingMode : watchedTrackingMode?.value) === "HOLDINGS_LEDGER";
  const isSettlementEditable = !isEdit || !hasLedgerCommands;

  const { data: accountsData } = useGetAccountsQuery(undefined, {
    skip: !isSettlementShown || !isSettlementEditable,
  });

  const settlementAccountOptions: SelectOptionTypes[] = useMemo(() => {
    const options = (accountsData ?? [])
      .filter(
        (account) =>
          account.isActive &&
          SETTLEMENT_ACCOUNT_TYPES.includes(account.accountType),
      )
      .map((account) => ({ value: String(account.id), label: account.name }));
    const current = resetValues.settlementAccount;
    return current && !options.some((option) => option.value === current.value)
      ? [...options, current]
      : options;
  }, [accountsData, resetValues.settlementAccount]);

  const handleSettlementChange = (next: CashSettlement) => {
    setValue("cashSettlement", next, { shouldDirty: true });
    if (next === "OWN_CASH") {
      setValue("settlementAccount", null, { shouldDirty: true });
      clearErrors("settlementAccount");
    }
  };

  const settlement: SettlementFieldState = {
    isShown: isSettlementShown,
    isEditable: isSettlementEditable,
    value: watchedSettlement,
    account: watchedSettlementAccount,
    accountOptions: settlementAccountOptions,
    onChange: handleSettlementChange,
  };

  // Preselect the first type only when creating; edit mode already carries
  // the investment's current type.
  useEffect(() => {
    if (!isEdit && investmentTypes.length > 0) {
      setValue("investmentType", investmentTypes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investmentTypes]);

  const eyebrow = isEdit ? "EDIT INVESTMENT" : "NEW INVESTMENT";
  const desktopTitle = isEdit ? "Edit investment" : "Create investment";
  const panelTitle = watchedName || desktopTitle;
  const mobileTitle = isEdit ? "Edit investment" : "New investment";
  const desktopActionLabel = isEdit ? "Save changes" : "Create investment";
  const mobileActionLabel = isEdit ? "Save" : "Create";

  const buildDiscardDetails = (): ConfirmDetail => {
    const values = getValues();

    if (isEdit) {
      return {
        pill: { label: "INVESTMENT", variant: "DEFAULT" },
        title: values.name || investment?.name || "Investment",
        subtitle: joinDetailParts(
          values.investmentType?.label,
          formatDate(values.startDate, DateFormat.DMY_SPACE),
        ),
      };
    }

    return buildDraftDetails({
      noun: "investment",
      fieldValues: [
        values.name,
        values.institution,
        values.investmentType,
        values.startDate,
        values.settlementAccount,
      ],
      primaryLabel: values.name,
    });
  };

  const discard = {
    ...discardCopyOf(isEdit, "investment"),
    details: buildDiscardDetails,
  };

  const load = {
    status: formPanelStatusOf({
      isLoading: isLoadingTypes,
      isError: isTypesError,
    }),
    onRetry: refetch,
    loadingLabel: "Loading investment types…",
    errorMessage: TYPES_ERROR_MESSAGE,
  };

  const showsTrackingModeChoice = !isEdit;
  const readOnlyTrackingMode = isEdit ? trackingMode : undefined;

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel="Investment form"
      eyebrow={eyebrow}
      title={panelTitle}
      form={form}
      resetValues={resetValues}
      formId={FORM_ID}
      onSubmit={handleOnSubmit}
      submitLabel={desktopActionLabel}
      discard={discard}
      load={load}
      mobile={{
        title: mobileTitle,
        submitLabel: mobileActionLabel,
        fields: (
          <MobileInvestmentForm
            control={control}
            register={register}
            setValue={setValue}
            clearErrors={clearErrors}
            investmentTypes={investmentTypes}
            trackingModeOptions={TRACKING_MODE_OPTIONS}
            trackingMode={trackingMode}
            isEdit={isEdit}
            isSubmitting={isSubmitting}
            settlement={settlement}
          />
        ),
      }}
    >
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="investment-name">Name *</FieldLabel>
          </LabelRow>
          <FormInput
            id="investment-name"
            required
            rules={{
              required: "Name is required!",
              minLength: {
                value: 3,
                message: "Name should have more than 3 letters!",
              },
            }}
            name="name"
            control={control}
            handleClear={false}
            disabled={isSubmitting}
            placeholder="e.g. Trade Republic"
            autoFocus={true}
          />
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="investment-type">Investment type *</FieldLabel>
          </LabelRow>
          <SelectForm
            id="investment-type"
            name="investmentType"
            control={control}
            rules={{ required: "Investment Type is required!" }}
            defaultValue={null}
            options={investmentTypes}
            disabled={isSubmitting}
          />
          <FieldHelper>
            Drives how this holding is grouped in your allocation.
          </FieldHelper>
        </Field>

        {showsTrackingModeChoice && (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="investment-tracking-mode">
                Tracking method *
              </FieldLabel>
            </LabelRow>
            <SelectForm
              id="investment-tracking-mode"
              name="trackingMode"
              control={control}
              rules={{ required: "Tracking method is required!" }}
              defaultValue={null}
              options={TRACKING_MODE_OPTIONS}
              disabled={isSubmitting}
              aria-describedby="investment-tracking-mode-help"
            />
            <FieldHelper id="investment-tracking-mode-help">
              {TRACKING_MODE_DESCRIPTIONS.MANUAL_MARKET_UPDATES}{" "}
              {TRACKING_MODE_DESCRIPTIONS.HOLDINGS_LEDGER}{" "}
              {TRACKING_MODE_IMMUTABLE_NOTE}
            </FieldHelper>
          </Field>
        )}

        {readOnlyTrackingMode && (
          <Field>
            <LabelRow>
              <FieldLabel>Tracking method</FieldLabel>
            </LabelRow>
            <ReadOnlyValue>
              {TRACKING_MODE_LABELS[readOnlyTrackingMode]}
            </ReadOnlyValue>
            <FieldHelper>{TRACKING_MODE_IMMUTABLE_NOTE}</FieldHelper>
          </Field>
        )}

        <CashSettlementField
          settlement={settlement}
          control={control}
          isSubmitting={isSubmitting}
        />
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="investment-institution">
              Institution
            </FieldLabel>
          </LabelRow>
          <FormInput
            id="investment-institution"
            name="institution"
            control={control}
            handleClear={false}
            disabled={isSubmitting}
            placeholder="e.g. DeGiro"
          />
          <FieldHelper>
            The broker, platform or provider holding this investment.
          </FieldHelper>
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="investment-start-date">
              Start date *
            </FieldLabel>
          </LabelRow>
          <InputDate
            id="investment-start-date"
            rules={{ required: "Start date is required!" }}
            name="startDate"
            control={control}
            disabled={isSubmitting}
            maxDate={new Date()}
            placeholder="YYYY/MM/DD"
          />
          <FieldHelper>When you first funded this investment.</FieldHelper>
        </Field>
      </FormCard>
    </FormPanel>
  );
};
